import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Separator } from "./ui/separator";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Loader2, Search, ChevronLeft, ChevronRight, ArrowRightLeft } from "lucide-react";

import {
  searchOpenEpdAll,
  getOpenEpdRecordById,
  getOpenEpdStatistics,
  fetchOpenEpdCategoriesTree,
  fetchOpenEpdProxyMeta,
  refineClientSide,
  listImpactMethods,
  listImpactKeys,
  extractImpactRows,
  pickBestMethod,
  extractListExtrasFromRecord
} from "../services/openepdService";

const SAMPLE_QUERIES = ["concrete", "timber", "insulation", "steel", "gypsum"];

function buildCategoryFlatWithLeaves(nodes, trail = []) {
  const out = [];
  const arr = Array.isArray(nodes) ? nodes : nodes ? [nodes] : [];

  function walk(node, parentTrail) {
    const name = node?.name || "";
    const shortName = node?.short_name || "";
    const openepdName = node?.openepd_hierarchical_name || "";
    const id = node?.id || "";
    const seg = (shortName || name || id || "").trim();

    const nextTrail = [...parentTrail, seg].filter(Boolean);
    const labelFull = nextTrail.join(" > ");
    const labelShort = seg || labelFull;

    const kids = Array.isArray(node?.subcategories) ? node.subcategories : [];
    const childLeafLists = kids.map((k) => walk(k, nextTrail));
    const leafIds = kids.length ? childLeafLists.flatMap((x) => x).filter(Boolean) : [String(id)].filter(Boolean);

    out.push({
      id: String(id || labelFull),
      name,
      shortName,
      openepdName,
      labelFull,
      labelShort,
      leafIds,
      depth: nextTrail.length
    });

    return leafIds;
  }

  for (const n of arr) walk(n, trail);

  return out;
}

function resolveCategoryFromTerm(term, flat) {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return null;
  const list = Array.isArray(flat) ? flat : [];

  const exactLabel = list.find((c) => (c.labelFull || "").toLowerCase() === t);
  if (exactLabel) return exactLabel;

  const exactId = list.find((c) => (c.id || "").toLowerCase() === t);
  if (exactId) return exactId;

  const exactShort = list.find((c) => (c.labelShort || c.shortName || "").toLowerCase() === t);
  if (exactShort) return exactShort;

  const exactName = list.find((c) => (c.name || "").toLowerCase() === t);
  if (exactName) return exactName;

  const exactOpen = list.find((c) => (c.openepdName || "").toLowerCase() === t);
  if (exactOpen) return exactOpen;

  const partial = list.find((c) => {
    const lbl = (c.labelFull || "").toLowerCase();
    const a = (c.shortName || "").toLowerCase();
    const b = (c.name || "").toLowerCase();
    const d = (c.openepdName || "").toLowerCase();
    return lbl.includes(t) || a.includes(t) || b.includes(t) || d.includes(t);
  });
  return partial || null;
}

function CategoryAutocomplete({ value, onChange, options, onPick, placeholder }) {
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = String(value || "").trim().toLowerCase();
    const list = Array.isArray(options) ? options : [];
    if (!q) return list.slice(0, 40);

    return list
      .filter((c) => {
        const a = String(c.labelShort || "").toLowerCase();
        const b = String(c.labelFull || "").toLowerCase();
        const d = String(c.openepdName || "").toLowerCase();
        return a.includes(q) || b.includes(q) || d.includes(q);
      })
      .slice(0, 60);
  }, [value, options]);

  return (
    <div className="relative w-full">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        placeholder={placeholder}
        className="pl-9 pr-12 h-9 text-xs text-gray-900"
      />

      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-lg">
          <div className="max-h-[320px] overflow-auto">
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-emerald-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(c);
                  setOpen(false);
                }}
              >
                <div className="text-sm font-medium text-gray-900">{c.labelShort}</div>
                <div className="text-[11px] text-gray-500">{c.labelFull}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDateMaybe(v) {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function formatImpactValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const absValue = Math.abs(num);
  if (absValue >= 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (absValue >= 1) return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (absValue === 0) return "0";
  if (absValue < 0.001) return num.toExponential(2);
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatSelectionSummary(values, { fallback = "All" } = {}) {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  if (values.length <= 2) return values.join(", ");
  return `${values.slice(0, 2).join(", ")} +${values.length - 2}`;
}

const ResultItem = ({ item, isSelected, onSelect }) => {
  const kindLabel = item.kind === "generic" ? "Generic" : item.kind === "industry" ? "Industry" : "Product";

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={`w-full text-left p-4 rounded-xl border transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/60 flex flex-col gap-2 ${
        isSelected ? "border-emerald-500 bg-emerald-50/70" : "border-emerald-100 bg-white/80"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-gray-800 line-clamp-2">{item.displayName}</span>
        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[11px]">
          {kindLabel}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
        {item.manufacturer && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
            {item.manufacturer}
          </Badge>
        )}
        {item.country && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
            {item.country}
          </Badge>
        )}
        {item.declaredUnit && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
            {item.declaredUnit}
          </Badge>
        )}
      </div>

      <div className="text-xs text-gray-500">
        {item.locationLabel ? <span>{item.locationLabel}</span> : <span>Location not specified</span>}
        {item.validUntil ? <span>{`  |  Valid until ${formatDateMaybe(item.validUntil)}`}</span> : null}
      </div>
    </button>
  );
};

const ImpactTable = ({ rows, unit, declaredUnit, totalSelectedImpact }) => {
  return (
    <Card className="shadow-sm border-emerald-100/80 bg-white">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
            <span>Impacts by module</span>
            {unit && (
              <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                {unit}{declaredUnit ? ` / ${declaredUnit}` : ""}
              </Badge>
            )}
          </CardTitle>
          {totalSelectedImpact && (
            <Badge className="bg-emerald-600 text-white text-xs">
              Total {String(totalSelectedImpact.key || "").toUpperCase()}: {formatImpactValue(totalSelectedImpact.total)}
              {totalSelectedImpact.unit ? ` ${totalSelectedImpact.unit}` : ""}
              {declaredUnit ? ` / ${declaredUnit}` : ""}
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs">Shows modules present in this record</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-emerald-100/70">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50 text-emerald-700">
              <tr>
                <th className="text-left font-medium px-3 py-2">Module</th>
                <th className="text-right font-medium px-3 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.stage} className="odd:bg-white even:bg-emerald-50/40">
                  <td className="px-3 py-2 font-medium text-emerald-800">{r.stageLabel}</td>
                  <td className="px-3 py-2 text-right text-emerald-900">{formatImpactValue(r.mean)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-sm text-gray-500" colSpan={2}>
                    No module impacts available on this record
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const OpenEpdExplorer = ({ onShortlist, shortlist = [], onStatusChange, onRegisterRefresh }) => {
  const [connectionStatus, setConnectionStatus] = useState("loading");
  const [connectionError, setConnectionError] = useState(null);

  const [categoriesFlat, setCategoriesFlat] = useState([]);
  const [categoryResolveError, setCategoryResolveError] = useState(null);
  const [proxyMeta, setProxyMeta] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [pickedCategory, setPickedCategory] = useState(null);

  const [committedQuery, setCommittedQuery] = useState("");
  const [committedCategory, setCommittedCategory] = useState(null);

  const [includeGeneric, setIncludeGeneric] = useState(true);
  const [includeIndustry, setIncludeIndustry] = useState(true);

  const [pageSize] = useState(50);
  const [nameContains, setNameContains] = useState("");
  const [warnings, setWarnings] = useState({});

  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [declaredUnitFilter, setDeclaredUnitFilter] = useState("");

  const [typeFilter, setTypeFilter] = useState({
    product: true,
    generic: true,
    industry: true
  });

  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState(null);

  const [omfUsed, setOmfUsed] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [extrasByKey, setExtrasByKey] = useState({});
  const [totalCounts, setTotalCounts] = useState({ product: 0, generic: 0, industry: 0 });

  const [pageNumber, setPageNumber] = useState(1);

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsStatus, setStatsStatus] = useState("idle");

  const [methodKey, setMethodKey] = useState(null);
  const [impactKey, setImpactKey] = useState(null);

  const activeControllerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    (async () => {
      try {
        setConnectionStatus("loading");
        setConnectionError(null);

        const tree = await fetchOpenEpdCategoriesTree({ signal: controller.signal });
        const flat = buildCategoryFlatWithLeaves(tree);

        if (!alive) return;
        setCategoriesFlat(flat);
        setConnectionStatus("success");
      } catch (e) {
        if (controller.signal.aborted || !alive) return;
        setConnectionStatus("error");
        setConnectionError(String(e?.message || e));
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    (async () => {
      try {
        const meta = await fetchOpenEpdProxyMeta({ signal: controller.signal });
        if (!alive) return;
        setProxyMeta(meta);
      } catch {
        if (!alive) return;
        setProxyMeta(null);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const commitSearch = useCallback(() => {
    const raw = searchTerm.trim();

    if (pickedCategory?.id) {
      setCategoryResolveError(null);
      setCommittedQuery(pickedCategory.id);
      setCommittedCategory(pickedCategory);
      setPageNumber(1);
      return;
    }

    const resolved = resolveCategoryFromTerm(raw, categoriesFlat);

    if (!resolved) {
      setCategoryResolveError(
        raw
          ? `No OpenEPD category matched "${raw}". Pick a category from the list.`
          : "Type a category and pick it from the list."
      );
      setCommittedQuery("");
      setCommittedCategory(null);
      setPageNumber(1);
      return;
    }

    setCategoryResolveError(null);
    setCommittedQuery(resolved.id);
    setCommittedCategory(resolved);
    setPickedCategory(resolved);
    setSearchTerm(resolved.labelShort);
    setPageNumber(1);
  }, [searchTerm, categoriesFlat, pickedCategory]);

  const canSearch = useMemo(() => {
    return !!committedQuery;
  }, [committedQuery]);

  useEffect(() => {
    const q = committedQuery.trim();
    if (!q) {
      setSearchStatus("idle");
      setSearchError(null);
      setAllItems([]);
      setTotalCounts({ product: 0, generic: 0, industry: 0 });
      setOmfUsed("");
      setStats(null);
      setStatsStatus("idle");
      setWarnings({});
      setCommittedCategory(null);
      return;
    }

    if (activeControllerRef.current) activeControllerRef.current.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;

    setSearchStatus("loading");
    setSearchError(null);
    setWarnings({});

    setSelectedItem(null);
    setDetail(null);
    setDetailStatus("idle");
    setDetailError(null);

    setStats(null);
    setStatsStatus("idle");

    (async () => {
      try {
        const r = await searchOpenEpdAll({
          categoryTerm: q,
          categoryIds: committedCategory?.leafIds || null,
          pageNumber,
          pageSize,
          includeGeneric,
          includeIndustry,
          signal: controller.signal
        });

        setOmfUsed(Array.isArray(r.omfs) ? r.omfs.join("\n") : String(r.omfs || ""));
        setWarnings({});

        const merged = []
          .concat(r.product?.items || [])
          .concat(r.generic?.items || [])
          .concat(r.industry?.items || []);

        setTotalCounts({
          product: r.product?.totalCount || 0,
          generic: r.generic?.totalCount || 0,
          industry: r.industry?.totalCount || 0
        });

        setAllItems(merged);
        setSearchStatus("success");
        setSearchError(null);

        if (Array.isArray(r.omfs) && r.omfs.length === 1) {
          try {
            setStatsStatus("loading");
            const s = await getOpenEpdStatistics({ omf: r.omfs[0], signal: controller.signal });
            setStats(s);
            setStatsStatus("success");
          } catch {
            setStatsStatus("error");
          }
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        setSearchStatus("error");
        setSearchError(String(e?.message || e));
        setWarnings({});
      }
    })();

    return () => controller.abort();
  }, [committedQuery, pageNumber, pageSize, includeGeneric, includeIndustry, committedCategory]);

  const enrichedItems = useMemo(() => {
    const list = Array.isArray(allItems) ? allItems : [];
    return list.map((it) => {
      const k = `${it.kind}:${it.id}`;
      const extras = extrasByKey[k];
      return extras ? { ...it, ...extras } : it;
    });
  }, [allItems, extrasByKey]);

  const filteredItems = useMemo(() => {
    const items = enrichedItems || [];
    const enabledKinds = [];
    if (typeFilter.product) enabledKinds.push("product");
    if (typeFilter.generic) enabledKinds.push("generic");
    if (typeFilter.industry) enabledKinds.push("industry");

    return refineClientSide(items, {
      manufacturer: manufacturerFilter,
      country: countryFilter,
      declaredUnit: declaredUnitFilter,
      nameContains
    }).filter((it) => enabledKinds.includes(it.kind));
  }, [enrichedItems, manufacturerFilter, countryFilter, declaredUnitFilter, nameContains, typeFilter]);

  const countryOptions = useMemo(() => {
    const set = new Set();
    for (const it of enrichedItems || []) if (it.country) set.add(String(it.country).toUpperCase());
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [enrichedItems]);

  const declaredUnitOptions = useMemo(() => {
    const set = new Set();
    for (const it of enrichedItems || []) if (it.declaredUnit) set.add(String(it.declaredUnit));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [enrichedItems]);

  const manufacturerOptions = useMemo(() => {
    const set = new Set();
    for (const it of enrichedItems || []) if (it.manufacturer) set.add(String(it.manufacturer));
    return Array.from(set).sort((a, b) => a.localeCompare(b)).slice(0, 50);
  }, [enrichedItems]);

  const handleSelect = useCallback(async (item) => {
    if (!item?.id) return;

    setSelectedItem(item);
    setDetail(null);
    setDetailStatus("loading");
    setDetailError(null);

    const controller = new AbortController();

    try {
      const fields = [
        "product_name",
        "name",
        "declared_unit",
        "org",
        "plant",
        "valid_until",
        "date_of_issue",
        "declaration_url",
        "program_operator_doc_id",
        "impacts"
      ];

      const epd = await getOpenEpdRecordById({
        id: item.id,
        kind: item.kind,
        fields,
        signal: controller.signal
      });

      setDetail(epd);
      setDetailStatus("success");

      const extras = extractListExtrasFromRecord(epd?.payload ?? epd);
      if (extras && (extras.manufacturer || extras.country || extras.declaredUnit || extras.validUntil)) {
        setExtrasByKey((prev) => ({
          ...prev,
          [`${item.kind}:${item.id}`]: extras
        }));
      }

      const mk = pickBestMethod(epd);
      setMethodKey(mk);

      const keys = mk ? listImpactKeys(epd, mk) : [];
      const defaultImpact = keys.includes("gwp") ? "gwp" : keys.includes("odp") ? "odp" : keys[0] || null;
      setImpactKey(defaultImpact);
    } catch (e) {
      setDetailStatus("error");
      setDetailError(String(e?.message || e));
    }

    return () => controller.abort();
  }, []);

  const methods = useMemo(() => listImpactMethods(detail), [detail]);
  const impactKeys = useMemo(() => (methodKey ? listImpactKeys(detail, methodKey) : []), [detail, methodKey]);

  const declaredUnitLabel = useMemo(() => {
    const du = detail?.declared_unit;
    if (typeof du === "string") return du;
    if (du && typeof du === "object") return du.unit || du.name || null;
    return null;
  }, [detail]);

  const totalSelectedImpact = useMemo(() => {
    if (!detail || !methodKey || !impactKey) return null;
    const { rows, unit } = extractImpactRows(detail, methodKey, impactKey);
    if (!rows.length) return null;
    const total = rows.reduce((sum, r) => sum + (Number.isFinite(Number(r.mean)) ? Number(r.mean) : 0), 0);
    return { total, unit, key: impactKey };
  }, [detail, methodKey, impactKey]);

  const shortlistImpact = useMemo(() => {
    if (!detail || !methodKey) return null;
    const gwp = extractImpactRows(detail, methodKey, "gwp");
    if (gwp.rows.length) return { ...gwp, key: "gwp" };
    if (impactKey) {
      const chosen = extractImpactRows(detail, methodKey, impactKey);
      if (chosen.rows.length) return { ...chosen, key: impactKey };
    }
    return null;
  }, [detail, methodKey, impactKey]);

  const shortlistModules = useMemo(() => {
    if (!shortlistImpact?.rows?.length) return null;
    const out = {};
    for (const r of shortlistImpact.rows) {
      const key = String(r.stageLabel || r.stage || "").trim();
      if (!key) continue;
      const value = Number(r.mean);
      if (!Number.isFinite(value)) continue;
      out[key] = value;
    }
    return Object.keys(out).length ? out : null;
  }, [shortlistImpact]);

  const shortlistGwpTotal = useMemo(() => {
    if (!shortlistImpact?.rows?.length) return 0;
    return shortlistImpact.rows.reduce(
      (sum, r) => sum + (Number.isFinite(Number(r.mean)) ? Number(r.mean) : 0),
      0
    );
  }, [shortlistImpact]);

  const selectedImpactData = useMemo(() => {
    if (!detail || !methodKey || !impactKey) return { unit: null, rows: [] };
    return extractImpactRows(detail, methodKey, impactKey);
  }, [detail, methodKey, impactKey]);

  const isAlreadyShortlisted = useMemo(() => {
    const id = selectedItem?.id;
    if (!id) return false;
    const uuid = `openepd:${selectedItem?.kind || "product"}:${selectedItem?.id}`;
    return Array.isArray(shortlist) && shortlist.some((s) => String(s?.uuid) === String(uuid));
  }, [selectedItem, shortlist]);

  const canGoPrev = pageNumber > 1;
  const canGoNext = canSearch && (allItems?.length || 0) >= pageSize;

  return (
    <div className="space-y-6">
      <div className="space-y-2">

        <div className="space-y-0 pt-1 sm:pt-0">
          <div className="flex flex-col gap-2" style={{ marginBottom: "10px" }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                <CategoryAutocomplete
                  value={searchTerm}
                  onChange={(v) => {
                    setSearchTerm(v);
                    setPickedCategory(null);
                  }}
                  options={categoriesFlat}
                  placeholder="Select a category, then click Search"
                  onPick={(cat) => {
                    setPickedCategory(cat);
                    setSearchTerm(cat.labelShort);
                  }}
                />

                {searchStatus === "loading" && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
                )}
              </div>

              <Input
                value={nameContains}
                onChange={(e) => setNameContains(e.target.value)}
                placeholder="Optional search"
                className="h-9 w-full sm:w-[180px] text-xs text-gray-900"
              />

              <Button
                type="button"
                className="h-9 px-3 bg-emerald-600 text-white hover:bg-emerald-700 w-full sm:w-auto"
                disabled={searchStatus === "loading"}
                onClick={commitSearch}
              >
                Search
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600" style={{ marginBottom: "16px" }}>
            {SAMPLE_QUERIES.map((sample) => (
              <Badge
                key={sample}
                onClick={() => {
                  setSearchTerm(sample);
                  setPickedCategory(null);
                }}
                className={`bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 shadow-none py-1 px-2.5 ${String(sample || "").toLowerCase() === "gypsum" ? "hidden sm:inline-flex" : ""}`}
              >
                {sample}
              </Badge>
            ))}
          </div>

          <div className="text-xs text-gray-600 mt-2" style={{ marginBottom: "12px" }}>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span>Types</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {formatSelectionSummary(
                        ["product", "generic", "industry"].filter((k) => typeFilter[k]).map((k) => k)
                      )}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Types</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {["product", "generic", "industry"].map((k) => (
                    <DropdownMenuCheckboxItem
                      key={k}
                      checked={typeFilter[k]}
                      className="text-xs"
                      onCheckedChange={(checked) => setTypeFilter((prev) => ({ ...prev, [k]: !!checked }))}
                    >
                      {k}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setTypeFilter({ product: true, generic: true, industry: true });
                    }}
                  >
                    Reset
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={countryOptions.length === 0}
                  >
                    <span>Country</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {countryFilter ? countryFilter : "All"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Country</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={(e) => {
                      e.preventDefault();
                      setCountryFilter("");
                    }}
                  >
                    All
                  </DropdownMenuItem>
                  {countryOptions.map((c) => (
                    <DropdownMenuItem
                      key={c}
                      className="text-xs"
                      onSelect={(e) => {
                        e.preventDefault();
                        setCountryFilter(c);
                      }}
                    >
                      {c}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={declaredUnitOptions.length === 0}
                  >
                    <span>Declared unit</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {declaredUnitFilter ? declaredUnitFilter : "All"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Declared unit</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={(e) => {
                      e.preventDefault();
                      setDeclaredUnitFilter("");
                    }}
                  >
                    All
                  </DropdownMenuItem>
                  {declaredUnitOptions.slice(0, 50).map((u) => (
                    <DropdownMenuItem
                      key={u}
                      className="text-xs"
                      onSelect={(e) => {
                        e.preventDefault();
                        setDeclaredUnitFilter(u);
                      }}
                    >
                      {u}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={manufacturerOptions.length === 0}
                  >
                    <span>Manufacturer</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {manufacturerFilter ? manufacturerFilter : "All"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Manufacturer</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={(e) => {
                      e.preventDefault();
                      setManufacturerFilter("");
                    }}
                  >
                    All
                  </DropdownMenuItem>
                  {manufacturerOptions.map((m) => (
                    <DropdownMenuItem
                      key={m}
                      className="text-xs"
                      onSelect={(e) => {
                        e.preventDefault();
                        setManufacturerFilter(m);
                      }}
                    >
                      {m}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {warnings && (warnings.generic || warnings.industry) && (
            <Alert>
              <AlertTitle>Some sources could not be loaded</AlertTitle>
              <AlertDescription className="text-xs">
                {warnings.generic ? `Generic: ${warnings.generic}` : null}
                {warnings.industry ? ` Industry: ${warnings.industry}` : null}
              </AlertDescription>
            </Alert>
          )}

          {categoryResolveError && (
            <Alert variant="destructive">
              <AlertTitle>Category not found</AlertTitle>
              <AlertDescription>{categoryResolveError}</AlertDescription>
            </Alert>
          )}

          {searchStatus === "error" && (
            <Alert variant="destructive">
              <AlertTitle>Search error</AlertTitle>
              <AlertDescription>{searchError}</AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-gray-500 mt-6 text-justify sm:text-left">
            Note: OpenEPD search uses server side search term, then applies country, declared unit and manufacturer filters client side for stability.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <Card className="bg-white border border-emerald-100 shadow self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800">Matching products</CardTitle>
            <CardDescription>
              {searchStatus === "idle"
                ? "Enter at least two characters to search."
                : searchStatus === "loading"
                ? "Searching..."
                : filteredItems.length
                ? `Showing ${filteredItems.length} items`
                : "No results"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1 min-h-0 mt-2 pr-2">
              <div className="space-y-3">
                {searchStatus === "idle" && !committedQuery ? (
                  <div className="text-sm text-gray-400 bg-emerald-50/30 border border-dashed border-emerald-200/50 rounded-xl p-4">
                    Select a category and click Search, or use the filters to explore. Try "concrete", "timber", or
                    "insulation".
                  </div>
                ) : searchStatus === "loading" && filteredItems.length === 0 ? (
                  <div className="space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={`ghost-${i}`}
                        className="rounded-xl border border-emerald-100 bg-white/70 p-4 animate-pulse"
                      >
                        <div className="h-4 w-2/3 bg-emerald-100 rounded mb-3" />
                        <div className="h-3 w-1/2 bg-emerald-50 rounded mb-2" />
                        <div className="h-3 w-1/3 bg-emerald-50 rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  filteredItems.map((it) => (
                    <ResultItem
                      key={`${it.kind}:${it.id}:${it.displayName}`}
                      item={it}
                      isSelected={String(selectedItem?.id) === String(it.id) && String(selectedItem?.kind) === String(it.kind)}
                      onSelect={handleSelect}
                    />
                  ))
                )}
              </div>
            </ScrollArea>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
              <span className="font-medium text-emerald-900">Page {pageNumber}</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={!canGoPrev || searchStatus === "loading"}
                  className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setPageNumber((p) => p + 1)}
                  disabled={!canGoNext || searchStatus === "loading"}
                  className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-emerald-100 self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
              Product impact details
            </CardTitle>
            <CardDescription>Select a record to load full details and module impacts.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1 min-h-0 pr-2">
              <div className="space-y-5">
                {detailStatus === "idle" && (
                  <div className="text-sm text-gray-400 bg-emerald-50/30 border border-emerald-200/50 rounded-xl p-4">
                    Choose an item on the left to load full OpenEPD details.
                  </div>
                )}

                {detailStatus === "loading" && (
                  <div className="flex items-center gap-3 text-emerald-700">
                    <Loader2 className="w-5 h-5 animate-spin" /> Fetching record details...
                  </div>
                )}

                {detailStatus === "error" && (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load record</AlertTitle>
                    <AlertDescription>{detailError}</AlertDescription>
                  </Alert>
                )}

                {detailStatus === "success" && detail && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 space-y-2">
                      <h3 className="text-lg font-semibold text-emerald-900">
                        {detail.product_name || detail.name || "OpenEPD record"}
                      </h3>

                      <div className="text-sm text-gray-700 space-y-1">
                        <p>Manufacturer: {detail?.org?.name || "Not specified"}</p>
                        <p>Location: {detail?.plant?.location ? [
                          detail.plant.location.city,
                          detail.plant.location.region,
                          detail.plant.location.country
                        ].filter(Boolean).join(", ") : "Not specified"}</p>
                        <p>Valid until: {detail.valid_until ? formatDateMaybe(detail.valid_until) : "Not specified"}</p>
                        {declaredUnitLabel && <p>Declared unit: {declaredUnitLabel}</p>}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {detail.declaration_url && (
                          <a
                            href={detail.declaration_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-emerald-700 underline"
                          >
                            Open declaration
                          </a>
                        )}
                        {onShortlist && selectedItem?.id && (
                          <div className="pt-1">
                            {!isAlreadyShortlisted ? (
                              <Button
                                type="button"
                                className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
                                onClick={() => {
                                  const uuid = `openepd:${selectedItem.kind || "product"}:${selectedItem.id}`;
                                  onShortlist({
                                    uuid,
                                    source: "openepd",
                                    kind: selectedItem.kind,
                                    id: selectedItem.id,
                                    name: detail.product_name || detail.name || selectedItem.displayName,
                                    declaredUnit: declaredUnitLabel ? { unit: declaredUnitLabel, amount: 1 } : null,
                                    gwpTotal: shortlistGwpTotal,
                                    modules: shortlistModules,
                                    manufacturer: detail?.org?.name || null,
                                    geography: detail?.plant?.location?.country || null,
                                    raw: detail
                                  });
                                }}
                              >
                                Shortlist
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                className="h-9 border-red-300 text-red-700 bg-white hover:bg-red-50"
                                onClick={() =>
                                  onShortlist({
                                    uuid: `openepd:${selectedItem.kind || "product"}:${selectedItem.id}`,
                                    source: "openepd",
                                    id: selectedItem.id,
                                    __remove: true
                                  })
                                }
                              >
                                Remove from shortlist
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Separator className="bg-emerald-100" />
                      <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Impact categories</h4>

                      <div className="flex flex-wrap items-center gap-2">
                        <label className="text-xs text-gray-600">Method</label>
                        <select
                          value={methodKey || ""}
                          onChange={(e) => {
                            const next = e.target.value || null;
                            setMethodKey(next);
                            const keys = next ? listImpactKeys(detail, next) : [];
                            const def = keys.includes("gwp") ? "gwp" : keys[0] || null;
                            setImpactKey(def);
                          }}
                          className="border border-emerald-200 rounded px-2 py-1 text-xs bg-white text-emerald-800"
                        >
                          {methods.length === 0 ? <option value="">No methods</option> : null}
                          {methods.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>

                        <label className="text-xs text-gray-600">Impact</label>
                        <select
                          value={impactKey || ""}
                          onChange={(e) => setImpactKey(e.target.value || null)}
                          className="border border-emerald-200 rounded px-2 py-1 text-xs bg-white text-emerald-800"
                        >
                          {impactKeys.length === 0 ? <option value="">No impacts</option> : null}
                          {impactKeys.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </select>
                      </div>

                      <ImpactTable
                        rows={selectedImpactData.rows}
                        unit={selectedImpactData.unit}
                        declaredUnit={declaredUnitLabel}
                        totalSelectedImpact={totalSelectedImpact}
                      />

                      {statsStatus === "success" && stats && (
                        <details className="rounded-xl border border-emerald-100 bg-white p-3">
                          <summary className="cursor-pointer text-xs text-emerald-700 font-semibold">
                            Benchmark stats for current search
                          </summary>
                          <pre className="mt-2 text-[11px] text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(stats, null, 2)}
                          </pre>
                        </details>
                      )}

                      <details className="rounded-xl border border-emerald-100 bg-white p-3">
                        <summary className="cursor-pointer text-xs text-emerald-700 font-semibold">Debug raw payload</summary>
                        <pre className="mt-2 text-[11px] text-gray-700 whitespace-pre-wrap">{JSON.stringify(detail, null, 2)}</pre>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OpenEpdExplorer;
